import { useState, useEffect } from 'react';
import { Modal, Input, Select, Button } from '@nexora/design-system';
import {
  type Account,
  type AccountInput,
} from './useAccounting';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'asset', label: 'Ativo' },
  { value: 'liability', label: 'Passivo' },
  { value: 'equity', label: 'Patrimonio Liquido' },
  { value: 'revenue', label: 'Receita' },
  { value: 'expense', label: 'Despesa' },
];

interface AccountFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  account?: Account;
  accounts: Account[];
  onSubmit: (data: AccountInput) => Promise<void>;
}

function flattenAccounts(list: Account[], level = 0): { value: string; label: string }[] {
  const result: { value: string; label: string }[] = [];
  for (const a of list) {
    result.push({ value: a.id, label: `${' '.repeat(level * 2)}${a.code} - ${a.name}` });
    if (a.children) result.push(...flattenAccounts(a.children, level + 1));
  }
  return result;
}

export function AccountFormModal({
  isOpen,
  onClose,
  account,
  accounts,
  onSubmit,
}: AccountFormModalProps): JSX.Element {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<string>('asset');
  const [parentId, setParentId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (account) {
      setCode(account.code);
      setName(account.name);
      setType(account.type);
      setParentId(account.parentId ?? '');
    } else {
      setCode('');
      setName('');
      setType('asset');
      setParentId('');
    }
    setError(null);
  }, [account, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!code.trim()) { setError('Código da conta e obrigatório.'); return; }
    if (!name.trim()) { setError('Nome da conta e obrigatório.'); return; }

    try {
      setSubmitting(true);
      await onSubmit({
        code: code.trim(),
        name: name.trim(),
        type: type as Account['type'],
        parentId: parentId || null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar conta.');
    } finally {
      setSubmitting(false);
    }
  };

  const parentOptions = flattenAccounts(accounts);
  const title = account ? 'Editar Conta' : 'Nova Conta';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input
            label="Código"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            placeholder="Ex: 1.1.1"
          />
          <Input
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Ex: Caixa"
          />
          <Select label="Tipo" value={type} onChange={(e) => setType(e.target.value)}>
            {ACCOUNT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Select
            label="Conta pai (opcional)"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">-- Nenhuma --</option>
            {parentOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          {error && (
            <p role="alert" style={{ margin: 0, fontSize: '0.8125rem', color: '#ba1a1a' }}>
              {error}
            </p>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e0e3e5',
          }}
        >
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={submitting}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}